import React from 'react';
import { Link } from 'react-router-dom';
import { categories, compounds } from '../data/compounds';
import CompoundCard from '../components/CompoundCard';
import './Home.css';

const topPriority = ['4-aco-dpt', 'mitragynine-pseudoindoxyl', 'tabernanthalog', 'mitragynine', '4-ho-met'];

export default function Home() {
  const totalCompounds = Object.keys(compounds).length;
  const unscheduled = Object.values(compounds).filter(c => c.schedulingStatus === 'unscheduled').length;
  const highBenefit = Object.values(compounds).filter(c => c.benefitScore >= 80).length;

  return (
    <div className="home">
      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-bg" />
        <div className="home-hero-content">
          <div className="home-hero-label">
            <span className="hero-label-dot" />
            Research Compound Intelligence Platform
          </div>
          <h1 className="home-hero-title">
            Advancing Research on<br />
            <span className="hero-title-accent">Underexplored Therapeutics</span>
          </h1>
          <p className="home-hero-subtitle">
            Wohler Chemical's mission: creating research-grade analogs of under-regulated or
            inaccessible substances for licensed university and clinical research. Each compound is
            evaluated for pharmacology, synthesizability, therapeutic potential, and legal status.
          </p>
          <div className="home-hero-actions">
            <Link to="/compounds" className="hero-btn-primary">Browse All Compounds →</Link>
            <Link to="/categories" className="hero-btn-secondary">View by Category</Link>
          </div>

          <div className="home-hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-value">{totalCompounds}</div>
              <div className="hero-stat-label">Research Compounds</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-value">{unscheduled}</div>
              <div className="hero-stat-label">Unscheduled (Federal)</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-value">{categories.length}</div>
              <div className="hero-stat-label">Therapeutic Categories</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-value">{highBenefit}</div>
              <div className="hero-stat-label">High Benefit Score (&ge;80)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="home-disclaimer">
        <span className="disclaimer-icon">⚠️</span>
        <div>
          <strong>Research Use Only.</strong> All compounds on this platform are intended for laboratory
          research purposes only, not for human consumption. Information is provided for scientific
          research and educational purposes. Scheduling status reflects federal DEA status at time of
          publication — verify current status before synthesis. State laws may differ.
        </div>
      </div>

      {/* Mission Statement */}
      <section className="home-mission">
        <div className="home-section-inner">
          <div className="home-mission-grid">
            <div className="mission-card">
              <div className="mission-card-icon">🔬</div>
              <h3>Research-Grade Quality</h3>
              <p>Every compound produced to research-grade purity standards with full analytical characterization (NMR, HPLC, MS).</p>
            </div>
            <div className="mission-card">
              <div className="mission-card-icon">⚖️</div>
              <h3>Legal Compliance First</h3>
              <p>All compounds are verified unscheduled under the DEA Controlled Substances Act. We monitor the Federal Register weekly.</p>
            </div>
            <div className="mission-card">
              <div className="mission-card-icon">🏛️</div>
              <h3>University Partnership</h3>
              <p>Compounds are supplied to IRB-registered university research groups for formal clinical and pharmacological investigation.</p>
            </div>
            <div className="mission-card">
              <div className="mission-card-icon">💡</div>
              <h3>Harm Reduction Focus</h3>
              <p>Priority given to compounds addressing opioid crisis, addiction, depression, and conditions with significant unmet need.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Priority Compounds */}
      <section className="home-priority">
        <div className="home-section-inner">
          <div className="home-section-header">
            <div className="home-section-label">Phase 1 Research Portfolio</div>
            <h2 className="home-section-title">Top Priority Compounds</h2>
            <p className="home-section-sub">
              Five compounds selected for immediate development based on therapeutic impact potential,
              synthesizability, and existing research infrastructure.
            </p>
          </div>
          <div className="home-priority-grid">
            {topPriority.map((id, i) => {
              const c = compounds[id];
              if (!c) return null;
              const cat = categories.find(cat => cat.id === c.categoryId);
              return (
                <div key={id} className="priority-card-wrapper">
                  <div className="priority-number" style={{ color: cat?.color }}>#{i + 1}</div>
                  <CompoundCard compound={c} showCategory />
                </div>
              );
            })}
          </div>
          <div className="home-priority-cta">
            <Link to="/compounds" className="hero-btn-secondary">View All {totalCompounds} Compounds →</Link>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="home-categories">
        <div className="home-section-inner">
          <div className="home-section-header">
            <div className="home-section-label">Therapeutic Areas</div>
            <h2 className="home-section-title">Research Categories</h2>
          </div>
          <div className="home-categories-grid">
            {categories.map(cat => (
              <Link key={cat.id} to={`/categories/${cat.id}`} className="home-cat-card" style={{ '--cat-color': cat.color, '--cat-color-light': cat.colorLight }}>
                <div className="home-cat-icon">{cat.icon}</div>
                <div className="home-cat-info">
                  <div className="home-cat-name">{cat.name}</div>
                  <div className="home-cat-count">{cat.compoundIds.length} compound{cat.compoundIds.length !== 1 ? 's' : ''}</div>
                </div>
                <div className="home-cat-arrow" style={{ color: cat.color }}>→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-section-inner">
          <div className="home-footer-inner">
            <div className="footer-brand">
              <span className="footer-brand-icon">⚗️</span>
              <span className="footer-brand-text">
                <span style={{ color: '#f1f5f9' }}>Wohler</span>
                <span style={{ color: '#6366f1' }}>.chemical</span>
              </span>
            </div>
            <div className="footer-links">
              <Link to="/about">About</Link>
              <Link to="/compounds">Compounds</Link>
              <Link to="/categories">Categories</Link>
            </div>
            <div className="footer-copy">
              © {new Date().getFullYear()} Wohler Chemical. All information for research purposes only.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}